import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Building2,
  DollarSign,
  Users,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  XCircle,
  LogOut,
  Crown,
  Database,
  Activity,
  Settings,
  Zap,
  Target,
  BarChart3,
  Navigation,
  Home
} from 'lucide-react';
import { toast } from 'sonner';
// Unused import removed
import BillingDashboard from './BillingDashboard';
import { AdminDebugPanel } from './AdminDebugPanel';
import SystemHealthMonitor from './superadmin/SystemHealthMonitor';
import ActivityFeed from './superadmin/ActivityFeed';
import TenantOnboarding from './superadmin/TenantOnboarding';
import RevenueAnalytics from './superadmin/RevenueAnalytics';
import CompanyManager from './superadmin/CompanyManager';
import NavigatorXAdminPanel from './superadmin/NavigatorXAdminPanel';
import { superAdminService } from '../utils/services/superAdminService';
import { GlobalSearch } from './superadmin/GlobalSearch';
import { AuthDebug } from './debug/AuthDebug';
import { collection, getDocs } from 'firebase/firestore';
import { db, navDb } from '../utils/firebase/client';
import { useNavigationHistory } from '../hooks/useNavigationHistory';
import ExitConfirmDialog from './ExitConfirmDialog';

interface SuperAdminDashboardProps {
  user: any;
  onLogout: () => void;
}

export default function SuperAdminDashboard({ user, onLogout }: SuperAdminDashboardProps) {
  const [activeView, setActiveView] = useState<'overview' | 'companies' | 'onboarding' | 'revenue' | 'activity' | 'health' | 'billing' | 'debug' | 'navigatorx'>('overview');
  const [stats, setStats] = useState({
    totalCompanies: 0,
    activeCompanies: 0,
    totalRevenue: 0,
    totalWells: 0,
    totalUsers: 0,
  });
  const [recentCompanies, setRecentCompanies] = useState<any[]>([]);
  const [navUserCount, setNavUserCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // 📱 Navigation History - Enable proper back button behavior for PWA
  const { showExitConfirmDialog, handleConfirmExit, handleCancelExit } = useNavigationHistory({
    activeTab: activeView,
    setActiveTab: setActiveView as (tab: string) => void,
    options: {
      defaultTab: 'overview',
      preventExit: true,
      showExitDialog: true
    }
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async (showLoadingState = true) => {
    try {
      if (showLoadingState) {
        setLoading(true);
      }

      const [platformStats, revenueMetrics] = await Promise.all([
        superAdminService.getPlatformStats(),
        superAdminService.getRevenueMetrics()
      ]);

      setStats({
        totalCompanies: platformStats.totalCompanies,
        activeCompanies: platformStats.activeTenants,
        totalWells: platformStats.totalWells,
        totalRevenue: revenueMetrics.mrr,
        totalUsers: platformStats.totalUsers,
      });

      // Load companies list for "Recent" table
      // We keep this direct for now as it's UI specific, or we could add getRecentCompanies to service
      const companiesRef = collection(db, 'companies');
      const snapshot = await getDocs(companiesRef);
      const companies = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      }));
      setRecentCompanies(companies.slice(0, 5));

      // Load NavigatorX user count (standalone app users)
      try {
        const navUsersRef = collection(navDb, 'nav_users');
        const navSnapshot = await getDocs(navUsersRef);
        setNavUserCount(navSnapshot.size);
      } catch (err) {
        console.log('NavigatorX users collection may not exist yet');
        setNavUserCount(0);
      }

    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      if (showLoadingState) {
        setLoading(false);
      }
    }
  };

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl flex items-center gap-3">
            <img src="/oilwellx-logo.png" alt="OilWellX" className="h-12 w-auto" />
            SuperAdmin Dashboard
          </h1>
          <p className="text-gray-500 mt-1">
            Manage all companies, users, and billing
          </p>
        </div>
        <div className="flex items-center gap-3 w-full md:w-auto">
          <div className="w-full md:w-64">
            <GlobalSearch />
          </div>
          <Badge variant="outline" className="text-purple-600 border-purple-600 hidden md:flex">
            <Crown className="h-4 w-4 mr-1" />
            Admin
          </Badge>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Building2 className="h-4 w-4 text-blue-600" />
              Total Companies
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl">{stats.totalCompanies}</div>
            <p className="text-sm text-gray-500 mt-1">
              {stats.activeCompanies} active
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-green-600" />
              Monthly Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl">${stats.totalRevenue.toLocaleString()}</div>
            <p className="text-sm text-gray-500 mt-1">
              From {stats.totalWells} wells
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Activity className="h-4 w-4 text-purple-600" />
              Total Wells
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl">{stats.totalWells}</div>
            <p className="text-sm text-gray-500 mt-1">
              Across all companies
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Users className="h-4 w-4 text-orange-600" />
              Total Users
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl">{stats.totalUsers}</div>
            <p className="text-sm text-gray-500 mt-1">
              Platform-wide
            </p>
          </CardContent>
        </Card>
      </div>

      {/* NavigatorX Section - Separate Box */}
      <Card className="border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-amber-500 p-2 rounded-lg">
                <Navigation className="h-5 w-5 text-white" />
              </div>
              <div>
                <span className="text-lg">NavigatorX Mobile App</span>
                <p className="text-sm text-gray-500 font-normal">Manage mobile app users & subscriptions</p>
              </div>
            </div>
            <Button
              onClick={() => setActiveView('navigatorx')}
              variant="amber"
            >
              View All Users
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg border border-amber-200">
              <p className="text-sm text-gray-500">Total Nav Users</p>
              <p className="text-2xl font-bold text-amber-600">{navUserCount}</p>
            </div>
            <div className="bg-white p-4 rounded-lg border border-amber-200">
              <p className="text-sm text-gray-500">Active Subs</p>
              <p className="text-2xl font-bold text-green-600">-</p>
            </div>
            <div className="bg-white p-4 rounded-lg border border-amber-200">
              <p className="text-sm text-gray-500">On Trial</p>
              <p className="text-2xl font-bold text-blue-600">-</p>
            </div>
            <div className="bg-white p-4 rounded-lg border border-amber-200">
              <p className="text-sm text-gray-500">Nav MRR</p>
              <p className="text-2xl font-bold text-green-600">$0.00</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Companies */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Recent Companies</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setActiveView('companies')}
            >
              View All
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : recentCompanies.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No companies yet
            </div>
          ) : (
            <div className="space-y-3">
              {recentCompanies.map((company) => (
                <div
                  key={company.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div
                      className="w-12 h-12 rounded-lg flex items-center justify-center text-white"
                      style={{ backgroundColor: company.primaryColor || '#3B82F6' }}
                    >
                      <Building2 className="h-6 w-6" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{company.companyName}</span>
                        {company.status === 'active' ? (
                          <Badge variant="outline" className="text-green-600 border-green-600">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-red-600 border-red-600">
                            <XCircle className="h-3 w-3 mr-1" />
                            Inactive
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">{company.subdomain}.oilwellx.com</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium">{company.wellCount || 0} wells</div>
                    <p className="text-sm text-gray-500">${((company.wellCount || 0) * 5).toFixed(2)}/mo</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* System Health */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5 text-blue-600" />
            System Health
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="font-medium">Database</span>
              </div>
              <Badge className="bg-green-600">Online</Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="font-medium">API Server</span>
              </div>
              <Badge className="bg-green-600">Running</Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="font-medium">Storage</span>
              </div>
              <Badge className="bg-green-600">Available</Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Crown className="h-6 w-6 text-purple-600" />
              <span className="text-xl">OilWellX SuperAdmin</span>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right mr-4">
                <p className="text-sm font-medium">{user?.name || user?.email}</p>
                <p className="text-xs text-gray-500">Super Administrator</p>
              </div>
              <Button
                variant="outline"
                onClick={onLogout}
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation Tabs */}
      <div className="bg-white border-b overflow-x-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 sm:gap-4 lg:gap-6 min-w-max">
            <button
              onClick={() => setActiveView('overview')}
              data-testid="nav-tab-overview"
              className={`py-3 sm:py-4 px-2 sm:px-3 border-b-2 transition-colors flex items-center gap-1 sm:gap-2 text-xs sm:text-sm whitespace-nowrap ${activeView === 'overview'
                ? 'border-purple-600 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
            >
              <Activity className="h-4 w-4" />
              <span className="hidden xs:inline">Overview</span>
            </button>
            <button
              onClick={() => setActiveView('companies')}
              data-testid="nav-tab-companies"
              className={`py-3 sm:py-4 px-2 sm:px-3 border-b-2 transition-colors flex items-center gap-1 sm:gap-2 text-xs sm:text-sm whitespace-nowrap ${activeView === 'companies'
                ? 'border-purple-600 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
            >
              <Building2 className="h-4 w-4" />
              <span className="hidden xs:inline">Companies</span>
            </button>
            <button
              onClick={() => setActiveView('onboarding')}
              data-testid="nav-tab-onboarding"
              className={`py-3 sm:py-4 px-2 sm:px-3 border-b-2 transition-colors flex items-center gap-1 sm:gap-2 text-xs sm:text-sm whitespace-nowrap ${activeView === 'onboarding'
                ? 'border-purple-600 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
            >
              <Target className="h-4 w-4" />
              <span className="hidden xs:inline">Onboard</span>
            </button>
            <button
              onClick={() => setActiveView('revenue')}
              data-testid="nav-tab-revenue"
              className={`py-3 sm:py-4 px-2 sm:px-3 border-b-2 transition-colors flex items-center gap-1 sm:gap-2 text-xs sm:text-sm whitespace-nowrap ${activeView === 'revenue'
                ? 'border-purple-600 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
            >
              <BarChart3 className="h-4 w-4" />
              <span className="hidden xs:inline">Revenue</span>
            </button>
            <button
              onClick={() => setActiveView('activity')}
              data-testid="nav-tab-activity"
              className={`py-3 sm:py-4 px-2 sm:px-3 border-b-2 transition-colors flex items-center gap-1 sm:gap-2 text-xs sm:text-sm whitespace-nowrap ${activeView === 'activity'
                ? 'border-purple-600 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
            >
              <Activity className="h-4 w-4" />
              <span className="hidden xs:inline">Activity</span>
            </button>
            <button
              onClick={() => setActiveView('health')}
              data-testid="nav-tab-health"
              className={`py-3 sm:py-4 px-2 sm:px-3 border-b-2 transition-colors flex items-center gap-1 sm:gap-2 text-xs sm:text-sm whitespace-nowrap ${activeView === 'health'
                ? 'border-purple-600 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
            >
              <Database className="h-4 w-4" />
              <span className="hidden xs:inline">Health</span>
            </button>
            <button
              onClick={() => setActiveView('billing')}
              data-testid="nav-tab-billing"
              className={`py-3 sm:py-4 px-2 sm:px-3 border-b-2 transition-colors flex items-center gap-1 sm:gap-2 text-xs sm:text-sm whitespace-nowrap ${activeView === 'billing'
                ? 'border-purple-600 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
            >
              <DollarSign className="h-4 w-4" />
              <span className="hidden xs:inline">Billing</span>
            </button>
            {/* NavigatorX Tab - Styled differently to stand out */}
            <button
              onClick={() => setActiveView('navigatorx')}
              data-testid="nav-tab-navigatorx"
              className={`py-3 sm:py-4 px-3 sm:px-4 border-b-2 transition-colors flex items-center gap-1 sm:gap-2 text-xs sm:text-sm whitespace-nowrap ${activeView === 'navigatorx'
                ? 'border-amber-500 text-amber-600 bg-amber-50'
                : 'border-transparent text-amber-600 hover:text-amber-700 hover:bg-amber-50'
                }`}
            >
              <Navigation className="h-4 w-4" />
              <span className="hidden xs:inline">NavigatorX</span>
            </button>
            <button
              onClick={() => setActiveView('debug')}
              data-testid="nav-tab-debug"
              className={`py-3 sm:py-4 px-2 sm:px-3 border-b-2 transition-colors flex items-center gap-1 sm:gap-2 text-xs sm:text-sm whitespace-nowrap ${activeView === 'debug'
                ? 'border-purple-600 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
            >
              <Settings className="h-4 w-4" />
              <span className="hidden xs:inline">Debug</span>
            </button>
          </div>
        </div>
      </div>

      {/* Content Area — NavigatorX panel gets full width (it has its own sidebar) */}
      {activeView === 'navigatorx' ? (
        <div className="flex-1 overflow-hidden" style={{ height: 'calc(100vh - 7.5rem)' }}>
          <NavigatorXAdminPanel />
        </div>
      ) : (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {activeView === 'overview' && renderOverview()}
          {activeView === 'companies' && (
            <CompanyManager
              onCompanyUpdate={() => loadDashboardData(false)}
            />
          )}
          {activeView === 'onboarding' && (
            <TenantOnboarding
              onComplete={() => {
                loadDashboardData(false);
                setActiveView('companies');
              }}
            />
          )}
          {activeView === 'revenue' && (
            <RevenueAnalytics />
          )}
          {activeView === 'activity' && (
            <ActivityFeed />
          )}
          {activeView === 'health' && (
            <SystemHealthMonitor />
          )}
          {activeView === 'billing' && (
            <BillingDashboard onClose={() => setActiveView('overview')} />
          )}
          {activeView === 'debug' && (
            <div className="space-y-6">
              <AdminDebugPanel showPanel={true} />
              <AuthDebug />
            </div>
          )}
        </div>
      )}

      {/* Exit Confirmation Dialog - shown when user presses back twice */}
      <ExitConfirmDialog
        open={showExitConfirmDialog}
        onConfirm={handleConfirmExit}
        onCancel={handleCancelExit}
      />
    </div>
  );
}